import { createSelector } from 'reselect';

import { initialMinusPhraseEditorState } from '../../reducers/minus-phrase-library-editor';
import { selectMinusPhrasesLibraryConstants } from '../../selectors/minusPhraseLibrary/minusPhraseLibrary';
import { ValidationErrorData } from '../../typings/common';
import { minusPhraseLibraryConstantsFragment } from '../../typings/graphql-grid';
import {
    MinusPhrasesLibraryItem,
    MinusPhrasesShortLibraryItem
} from '../../typings/minusPhraseLibrary';
import { AddLibraryListEditor } from '../../typings/minusPhrases';
import {
    MinusPhraseLibraryEditorData,
    MinusPhraseLibraryEditorState,
    State
} from '../../typings/state';

const DEFAULT_MINUS_PHRASES_FORM_EDIT_ERROR: ValidationErrorData[] = [];

export const selectMinusPhrasesEditorById = (
    state: MinusPhraseLibraryEditorState,
    editorId: string
): MinusPhraseLibraryEditorData => {
    if (!state.data) {
        return initialMinusPhraseEditorState;
    }

    return state.data[editorId] || initialMinusPhraseEditorState;
};

export const selectLibraryListItem = createSelector(
    (selectedLibraryList?: MinusPhrasesShortLibraryItem[]) =>
        selectedLibraryList,
    (selectedLibraryList: MinusPhrasesShortLibraryItem[]): string[] => {
        if (!selectedLibraryList) {
            return [];
        }

        return selectedLibraryList.map(item => item.name);
    }
);

export const selectIsAddLibraryListDisabled = createSelector(
    selectMinusPhrasesLibraryConstants,
    (state: State) => state.minusPhraseLibraryEditor.libraryItems,
    (
        constants?: minusPhraseLibraryConstantsFragment,
        libraryItems?: MinusPhrasesLibraryItem[]
    ): boolean => {
        if (!constants || !libraryItems) {
            return false;
        }

        return constants.maxLibraryPacksCount === libraryItems.length;
    }
);

export const selectOrderedLibraryLists = createSelector(
    (state: State, currentList: MinusPhrasesShortLibraryItem[]) => currentList,
    (state: State) => state.minusPhraseLibraryEditor.libraryItems,
    (
        selectedLibraryListItems: MinusPhrasesShortLibraryItem[],
        libraryItems?: MinusPhrasesLibraryItem[]
    ): MinusPhrasesLibraryItem[] | undefined => {
        if (!libraryItems) {
            return undefined;
        }

        if (selectedLibraryListItems.length === 0) {
            return libraryItems;
        }

        return libraryItems.slice().sort(
            (
                a: MinusPhrasesLibraryItem,
                b: MinusPhrasesLibraryItem
            ): number => {
                if (selectedLibraryListItems.find(item => item.id === a.id)) {
                    return -1;
                } else if (
                    selectedLibraryListItems.find(item => item.id === b.id)
                ) {
                    return 1;
                } else {
                    return 0;
                }
            }
        );
    }
);

export const selectLibraryListsInfo = createSelector(
    (state: State) =>
        Boolean(state.minusPhraseLibraryEditor.libraryItemsIsLoading),
    (state: State) =>
        Boolean(state.minusPhraseLibraryEditor.libraryItemsApiError),
    selectOrderedLibraryLists,
    (
        isLoading: boolean,
        apiError: boolean,
        libraryItems?: MinusPhrasesLibraryItem[]
    ): {
        isLoading: boolean;
        apiError: boolean;
        libraryItems: MinusPhrasesLibraryItem[] | undefined;
    } => {
        return {
            isLoading,
            apiError,
            libraryItems
        };
    }
);

export const selectGroupLibraryListEditorInfo = (
    state: State,
    editorId: string
): AddLibraryListEditor | undefined => {
    return state.minusPhraseLibraryEditor.data[editorId].addLibraryListEditor;
};

export const selectGroupLibraryListEditorErrors = createSelector(
    (state: State, editorId: string) =>
        state.minusPhraseLibraryEditor.data[editorId].addLibraryListEditor,
    (editorData?: AddLibraryListEditor): Array<ValidationErrorData> => {
        if (editorData && editorData.errors) {
            const errors = editorData.errors;

            return errors;
        }

        return DEFAULT_MINUS_PHRASES_FORM_EDIT_ERROR;
    }
);

export const selectGroupLibraryListEditorData = createSelector(
    selectGroupLibraryListEditorErrors,
    selectMinusPhrasesLibraryConstants,
    selectGroupLibraryListEditorInfo,
    (
        errors: Array<ValidationErrorData>,
        constants?: minusPhraseLibraryConstantsFragment,
        libraryListEditor?: AddLibraryListEditor
    ) => {
        if (!libraryListEditor) {
            return {
                isVisible: false,
                isLoading: true,
                listTitle: '',
                minusPhrases: '',
                errors
            };
        } else {
            const {
                isVisible,
                isLoading,
                listTitle,
                minusPhrases
            } = libraryListEditor;

            if (!constants) {
                return {
                    isVisible,
                    isLoading,
                    errors,
                    listTitle,
                    minusPhrases
                };
            }

            const { libMinusKeywordsTextMaxLength, maxNameLength } = constants;

            return {
                isVisible,
                isLoading,
                errors,
                listTitle,
                minusPhrases,
                listTitleCount: maxNameLength - listTitle.length,
                minusPhrasesCount:
                    libMinusKeywordsTextMaxLength - minusPhrases.length
            };
        }
    }
);
